import { useEffect, useRef } from "react";

export type ShortcutConfig = {
  shortcut: string;
  action: (event: KeyboardEvent) => void;
};

export function useShortcuts(shortcuts: ShortcutConfig[]) {
  const shortcutsRef = useRef(shortcuts);

  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    const activeKeys = new Set<string>();

    const handleKeyDown = (event: KeyboardEvent) => {
      // 🟩 FIX: Ignore OS key-repeat events so the shortcut only triggers once per physical press
      if (event.repeat) return;

      const key = event.key.toLowerCase() === " " ? "space" : event.key.toLowerCase();
      activeKeys.add(key);

      if (event.ctrlKey) activeKeys.add("control");
      if (event.shiftKey) activeKeys.add("shift");
      if (event.altKey) activeKeys.add("alt");
      if (event.metaKey) activeKeys.add("meta");

      for (const { shortcut, action } of shortcutsRef.current) {
        const targetKeys = shortcut
          .toLowerCase()
          .split("+")
          .map((k) => (k.trim() === " " ? "space" : k.trim()));

        const isMatch = targetKeys.every((k) => activeKeys.has(k));

        if (isMatch && activeKeys.size === targetKeys.length) {
          event.preventDefault();
          action(event);
          break;
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase() === " " ? "space" : event.key.toLowerCase();
      activeKeys.delete(key);

      if (!event.ctrlKey) activeKeys.delete("control");
      if (!event.shiftKey) activeKeys.delete("shift");
      if (!event.altKey) activeKeys.delete("alt");
      if (!event.metaKey) activeKeys.delete("meta");
    };

    const handleBlur = () => {
      activeKeys.clear();
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);
}
